import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { CheckCircle, XCircle, Eye, Clock, User, Mail, CreditCard } from 'lucide-react';
import { formatDateTime } from '../utils/datetime';

const PendingPaymentList = ({ 
  pendingRegistrations = [], 
  onApprove, 
  onReject,
  isLoading = false,
  className = "" 
}) => {
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  const handleApprove = async (registration) => {
    setActionLoading(registration.userId);
    try {
      if (onApprove) {
        await onApprove(registration);
      }
    } catch (error) {
      console.error('Approval error:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (registration) => {
    setActionLoading(registration.userId);
    try {
      if (onReject) {
        await onReject(registration);
      }
    } catch (error) {
      console.error('Rejection error:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending_payment':
        return <Badge variant="outline" className="text-yellow-600 border-yellow-600">Pending Payment</Badge>;
      case 'paid':
        return <Badge variant="outline" className="text-green-600 border-green-600">Paid</Badge>;
      case 'payment_failed':
        return <Badge variant="outline" className="text-red-600 border-red-600">Payment Failed</Badge>;
      default:
        return <Badge variant="outline">Unknown</Badge>;
    }
  };

  const formatDate = (dateString) => formatDateTime(dateString);

  if (pendingRegistrations.length === 0) {
    return (
      <Card className={className}>
        <CardContent className="p-8 text-center">
          <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Pending Payments</h3>
          <p className="text-gray-500">All registrations have been processed.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center space-x-2">
            <Clock className="w-5 h-5" />
            <span>Pending Payment Approvals</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-yellow-50 rounded-lg">
              <div className="text-2xl font-bold text-yellow-600">
                {pendingRegistrations.length}
              </div>
              <div className="text-sm text-yellow-700">Pending Approvals</div>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">
                ₹{pendingRegistrations.reduce((sum, reg) => sum + (reg.amount || 0), 0)}
              </div>
              <div className="text-sm text-blue-700">Total Amount</div>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">
                {pendingRegistrations.filter(reg => reg.status === 'paid').length}
              </div>
              <div className="text-sm text-green-700">Approved</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Registrations List */}
      <div className="space-y-4">
        {pendingRegistrations.map((registration) => (
          <Card key={registration.userId} className="border-l-4 border-l-yellow-400">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-3">
                    <User className="w-5 h-5 text-gray-400" />
                    <h3 className="text-lg font-medium text-gray-900">
                      {registration.formData?.['Name'] || registration.formData?.['Name'] || 'Unknown User'}
                    </h3>
                    {getStatusBadge(registration.status)}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <Mail className="w-4 h-4" />
                        <span>{registration.formData?.['Email'] || 'No email'}</span>
                      </div>
                      <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <CreditCard className="w-4 h-4" />
                        <span>Amount: ₹{registration.amount || 0}</span>
                      </div>
                      <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <Clock className="w-4 h-4" />
                        <span>Registered: {formatDate(registration.registeredAt)}</span>
                      </div>
                    </div>

                    <div className="space-y-2">
                      {registration.paymentProof && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-1">Payment Proof:</p>
                          <p className="text-sm text-gray-600 bg-gray-50 p-2 rounded border">
                            {registration.paymentProof}
                          </p>
                        </div>
                      )}
                      {registration.paymentMethod && (
                        <div>
                          <p className="text-sm font-medium text-gray-700">Payment Method:</p>
                          <p className="text-sm text-gray-600 capitalize">
                            {registration.paymentMethod}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Additional Form Data */}
                  {registration.formData && (
                    <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm font-medium text-gray-700 mb-2">Registration Details:</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                        {Object.entries(registration.formData)
                          .filter(([key]) => !['Name', 'Name', 'Email'].includes(key))
                          .map(([key, value]) => (
                            <div key={key} className="flex justify-between">
                              <span className="text-gray-600">{key}:</span>
                              <span className="text-gray-900 font-medium">{value}</span>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex flex-col space-y-2 ml-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedRegistration(registration)}
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    View Details
                  </Button>
                  
                  {registration.status === 'pending_payment' && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleApprove(registration)}
                        disabled={actionLoading === registration.userId}
                        className="text-green-600 border-green-600 hover:bg-green-50"
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        {actionLoading === registration.userId ? 'Approving...' : 'Approve'}
                      </Button>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleReject(registration)}
                        disabled={actionLoading === registration.userId}
                        className="text-red-600 border-red-600 hover:bg-red-50"
                      >
                        <XCircle className="w-4 h-4 mr-1" />
                        {actionLoading === registration.userId ? 'Rejecting...' : 'Reject'}
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Detail Modal */}
      {selectedRegistration && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Registration Details</CardTitle>
                <Button variant="ghost" onClick={() => setSelectedRegistration(null)}>
                  ✕
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium text-gray-700">User Information</h4>
                  <div className="mt-2 space-y-1 text-sm">
                    <p><strong>Name:</strong> {selectedRegistration.formData?.['Name'] || selectedRegistration.formData?.['Name']}</p>
                    <p><strong>Email:</strong> {selectedRegistration.formData?.['Email']}</p>
                    <p><strong>Status:</strong> {getStatusBadge(selectedRegistration.status)}</p>
                    <p><strong>Amount:</strong> ₹{selectedRegistration.amount}</p>
                  </div>
                </div>
                <div>
                  <h4 className="font-medium text-gray-700">Payment Information</h4>
                  <div className="mt-2 space-y-1 text-sm">
                    <p><strong>Method:</strong> {selectedRegistration.paymentMethod}</p>
                    <p><strong>Registered:</strong> {formatDate(selectedRegistration.registeredAt)}</p>
                    {selectedRegistration.paymentProof && (
                      <p><strong>Proof:</strong> {selectedRegistration.paymentProof}</p>
                    )}
                  </div>
                </div>
              </div>
              
              {selectedRegistration.formData && (
                <div>
                  <h4 className="font-medium text-gray-700 mb-2">Form Responses</h4>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    {Object.entries(selectedRegistration.formData).map(([key, value]) => (
                      <div key={key} className="flex justify-between py-1 border-b border-gray-200 last:border-b-0">
                        <span className="text-gray-600">{key}:</span>
                        <span className="text-gray-900 font-medium">{value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default PendingPaymentList;
